import { useMemo } from "react";
import {
  type InfiniteData,
  type QueryKey,
  useInfiniteQuery,
} from "@tanstack/react-query";
import {
  type EntityListOfCardTransactionItem,
  getCardTransactions,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";
import type { CardTransactionFilters } from "Components/CardTransactions/utils/cardTransactionFilterUtils";
import {
  cardFiltersToQueryParams,
  type CardTransactionQueryParams,
} from "Components/CardTransactions/utils/cardTransactionFilterUtils";

const CARD_TRANSACTIONS_QUERY_KEY = "CARD_TRANSACTIONS";

export const cardTransactionsKeys = {
  all: [CARD_TRANSACTIONS_QUERY_KEY],
  byFilters: (params: CardTransactionQueryParams) => [
    ...cardTransactionsKeys.all,
    params,
  ],
};

type Props = {
  filters: CardTransactionFilters;
  limit?: number;
  nextPageLimit?: number;
};

type PageParams = {
  offset: number;
  limit: number;
};

export const useCardTransactions = ({
  filters,
  limit = 10,
  nextPageLimit = 25,
}: Props) => {
  const queryParams = useMemo(
    () => cardFiltersToQueryParams(filters),
    [filters],
  );

  return useInfiniteQuery<
    EntityListOfCardTransactionItem,
    unknown,
    InfiniteData<EntityListOfCardTransactionItem>,
    QueryKey,
    PageParams
  >({
    queryKey: cardTransactionsKeys.byFilters(queryParams),
    queryFn: processResponseWithApiProblemDetails(
      ({ pageParam: { limit, offset } }) => {
        return getCardTransactions(
          offset,
          limit,
          queryParams.dateFrom,
          queryParams.dateTo,
          queryParams.payPointPublicID,
          queryParams.employeePublicID,
          queryParams.status,
          queryParams.isRefundOrCancellation,
        );
      },
    ),
    initialPageParam: { offset: 0, limit },
    getNextPageParam: (lastPage: EntityListOfCardTransactionItem | null) => {
      if (!lastPage) {
        return undefined;
      }

      const { offset, limit, totalCount } = lastPage;
      const isFinalPage = offset + limit >= totalCount;

      if (isFinalPage) {
        return undefined;
      }

      return { offset: offset + limit, limit: nextPageLimit };
    },
  });
};
